const TASK_ID_TEXT = /^[1-9]\d*$/;
const MAX_TASK_ID_DIGITS = 16; // Number.MAX_SAFE_INTEGER has 16 decimal digits.
const MAX_ENCODED_TASK_ID_LENGTH = 24;
const MAX_ENCODED_TASK_KEY_LENGTH = 1360; // 255 Unicode code points at four UTF-8 bytes each.

export function normalizeAssessmentTaskId(value: unknown): number | null {
    if (typeof value !== "number" &&
        (typeof value !== "string" || value.length > MAX_TASK_ID_DIGITS || !TASK_ID_TEXT.test(value))) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function isCanonicalTaskRouteKey(value: unknown): value is string {
    if (typeof value !== "string" || !value || value.trim() !== value ||
        value.length > 510 || /[\x00-\x1f\x7f]/.test(value)) return false;
    let codePoints = 0;
    for (const character of value) {
        const point = character.codePointAt(0)!;
        if ((point >= 0xd800 && point <= 0xdfff) || ++codePoints > 255) return false;
    }
    return true;
}

export function encodeAssessmentTaskId(value: unknown): string | null {
    const id = normalizeAssessmentTaskId(value);
    if (id === null) return null;
    try {
        return btoa(String(id));
    } catch {
        return null;
    }
}

export function decodeAssessmentTaskId(value: string | null | undefined): number | null {
    if (typeof value !== "string" || value.length > MAX_ENCODED_TASK_ID_LENGTH ||
        !/^[A-Za-z0-9+/]+={0,2}$/.test(value) ||
        value.length % 4 !== 0) return null;
    try {
        const decoded = atob(value);
        const id = normalizeAssessmentTaskId(decoded);
        return id !== null && btoa(decoded) === value ? id : null;
    } catch {
        return null;
    }
}

export interface AssessmentTaskRouteIntent {
    taskId: number;
    productLicenseId: number;
    section: string;
    target: string;
}

function decodeCanonicalTaskRouteKey(value: string | null): string | null {
    if (typeof value !== "string" || value.length > MAX_ENCODED_TASK_KEY_LENGTH ||
        !/^[A-Za-z0-9+/]+={0,2}$/.test(value) ||
        value.length % 4 !== 0) return null;
    try {
        const decoded = decodeURIComponent(escape(atob(value)));
        return isCanonicalTaskRouteKey(decoded) &&
            btoa(unescape(encodeURIComponent(decoded))) === value ? decoded : null;
    } catch {
        return null;
    }
}

export function parseAssessmentTaskRouteIntent({
    encodedTaskId, encodedLicenseId, encodedSection, encodedTarget,
    section, target, showPdfHistory
}: {
    encodedTaskId: string | null;
    encodedLicenseId: string | null;
    encodedSection: string | null;
    encodedTarget: string | null;
    section: string;
    target: string;
    showPdfHistory: boolean;
}): AssessmentTaskRouteIntent | null {
    const taskId = decodeAssessmentTaskId(encodedTaskId);
    const productLicenseId = decodeAssessmentTaskId(encodedLicenseId);
    return taskId !== null && productLicenseId !== null && !showPdfHistory &&
        decodeCanonicalTaskRouteKey(encodedSection) === section &&
        decodeCanonicalTaskRouteKey(encodedTarget) === target &&
        isCanonicalTaskRouteKey(section) && isCanonicalTaskRouteKey(target)
        ? { taskId, productLicenseId, section, target }
        : null;
}

export function taskIdForRouteSelection(
    intent: AssessmentTaskRouteIntent | null,
    productLicenseId: number | null,
    selection: { section: string; target: string } | null
): number | null {
    return intent && selection && intent.productLicenseId === productLicenseId &&
        intent.section === selection.section && intent.target === selection.target
        ? intent.taskId : null;
}
